import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { getFloatingInlineMessagePlacement } from '../../lib/floatingAnchor.js'
import { resolveFloatingLauncherColors } from './ChatLauncher.jsx'

const ANIMATION_MS = 300

const OFFSCREEN_TRANSFORM = {
  right: 'translateX(20px)',
  left: 'translateX(-20px)',
  top: 'translateY(-20px)',
  bottom: 'translateY(20px)',
}

/**
 * Free-shipping / abandoned-cart "slideout" bubble anchored next to the
 * floating launcher. v1 parity (`InlineChatMessage` with `relative=true`
 * in ometrics-ai): same corner as the launcher, directional slide-in from
 * the launcher's side, arrow pointing at it, launcher colors, click to
 * open chat. Rendered as a sibling of `ChatLauncher` (both fixed-position),
 * since v2's launcher has no shared relative-positioned parent box.
 */
export function FloatingInlineMessage({ conf, text, onOpen, onDismiss }) {
  const [visible, setVisible] = useState(false)
  const [dismissing, setDismissing] = useState(false)

  useEffect(() => {
    setDismissing(false)
    const timer = setTimeout(() => setVisible(true), 50)
    return () => clearTimeout(timer)
    // Re-trigger the slide-in whenever the message text changes.
  }, [text])

  if (!text) return null

  const { wrapperStyle, align, arrowSide, slideFrom } =
    getFloatingInlineMessagePlacement(conf || {})
  const { background, foreground } = resolveFloatingLauncherColors(conf || {})

  const handleDismiss = (e) => {
    e?.stopPropagation()
    setDismissing(true)
    setTimeout(() => onDismiss?.(), ANIMATION_MS)
  }

  const handleClick = () => {
    onOpen?.()
  }

  const arrowAlignClass =
    align === 'right' ? 'right-5' : align === 'left' ? 'left-5' : 'left-1/2 -translate-x-1/2'

  return (
    <div
      style={wrapperStyle}
      className="max-w-[min(200px,calc(100vw-32px))]"
    >
      <div
        onClick={onOpen ? handleClick : undefined}
        style={{
          backgroundColor: background,
          color: foreground,
          transform: visible && !dismissing ? 'translate(0, 0)' : OFFSCREEN_TRANSFORM[slideFrom],
          opacity: visible && !dismissing ? 1 : 0,
        }}
        className={cn(
          'relative flex min-w-[160px] max-w-full items-start gap-2 rounded-xl px-4 py-3 shadow-lg',
          'transition-[transform,opacity] duration-300 ease-out',
          onOpen ? 'cursor-pointer hover:shadow-xl' : 'cursor-default',
        )}
      >
        <p
          className="flex-1 text-sm leading-snug"
          style={{ color: foreground }}
        >
          {text}
        </p>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          style={{ color: foreground }}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            'hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
          )}
        >
          <X className="h-4 w-4" />
        </button>
        <span
          aria-hidden="true"
          className={cn('absolute h-0 w-0 border-x-8 border-x-transparent', arrowAlignClass)}
          style={
            arrowSide === 'bottom'
              ? { bottom: '-8px', borderTop: `8px solid ${background}` }
              : { top: '-8px', borderBottom: `8px solid ${background}` }
          }
        />
      </div>
    </div>
  )
}
